/* ============================================================
   WeLume 彫刻代の自動同期（カート ⇄ 彫刻商品の連動）
   ------------------------------------------------------------
   考え方：
   - 「彫刻された商品行」＝正（ソース・オブ・トゥルース）
   - 各商品行は隠しproperty「_彫刻料金ID」に、対応する料金variant
     （別商品「彫刻」の1列/2列）のIDを持っている
   - カート操作が確定するたびに、
       料金variantの数量 = その料金IDを持つ商品行の数量合計
     に「セット」する（/cart/update.js は絶対値セット＝二重加算しない）
   - これで「商品を消したら料金も消える／料金だけ消しても戻る／
     数量を増やしたら料金も増える」を自動実現する

   信頼性の要点：
   - 実行中に届いたイベントは“捨てず保留”し、必ずもう一度回す（取りこぼし防止）
   - 自分が出すUI更新イベントだけは識別して無視（無限ループ防止）
   - 更新後に検証パスを1回入れ、最終状態を必ず整合させる（“残ったまま”を根絶）
   ============================================================ */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const FEE_IDS = (window.WELUME_ENGRAVING_FEE_VARIANTS || []).map(String);
const FEE_PROP_KEYS = ['_彫刻料金ID', '_fee_id'];
const INTERNAL_FLAG = '__welumeEngravingSync';

let running = false;
let pending = false;

function cartBase() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
}

function readFeeId(item) {
  const p = item.properties || {};
  for (const k of FEE_PROP_KEYS) {
    if (p[k]) return String(p[k]);
  }
  return null;
}

function collectCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

// カートUIを更新（ベストエフォート）。自分のイベントには印を付け、リスナー側で無視する
function refreshCartUI(resp) {
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    const ev = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
    ev[INTERNAL_FLAG] = true;
    document.dispatchEvent(ev);
    let cartObj = null;
    try {
      cartObj = CartLinesUpdateEvent.createCartFromAjaxResponse(resp);
    } catch (e) {
      cartObj = { totalQuantity: resp.item_count };
    }
    deferred.resolve({ cart: cartObj, detail: { sections: resp.sections, itemCount: resp.item_count } });
  } catch (e) {
    /* UI更新に失敗しても、カート自体は既に正しい状態 */
  }
}

// 1回分：カート内容(items)を見て料金の過不足があれば update.js で是正。是正したら true を返す
// items が渡されればそれを使い（カート再取得を省略＝高速）、無ければ cart.js を読む
async function reconcileOnce(items) {
  if (!Array.isArray(items)) {
    try {
      const cart = await fetch(`${cartBase()}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
      items = cart && cart.items;
    } catch (e) {
      return false;
    }
  }
  if (!Array.isArray(items)) return false;
  const cart = { items };

  const needed = {};
  const current = {};
  FEE_IDS.forEach((id) => { needed[id] = 0; current[id] = 0; });
  for (const item of cart.items) {
    const fid = readFeeId(item);
    if (fid && Object.prototype.hasOwnProperty.call(needed, fid)) needed[fid] += item.quantity;
    const vid = String(item.variant_id || item.id);
    if (Object.prototype.hasOwnProperty.call(current, vid)) current[vid] += item.quantity;
  }

  const updates = {};
  let changed = false;
  FEE_IDS.forEach((id) => { updates[id] = needed[id]; if (needed[id] !== current[id]) changed = true; });
  if (!changed) return false;

  try {
    const body = { updates, sections: collectCartSectionIds().join(','), sections_url: window.location.pathname };
    const resp = await fetch(`${cartBase()}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());
    refreshCartUI(resp);
    return true;
  } catch (e) {
    return false;
  }
}

// 実行中に来たイベントは pending に積み、終わったらもう一度回す（取りこぼし防止）
// items があれば初回パスはそれを使い、カート再取得を省略して高速化
async function reconcile(items) {
  if (FEE_IDS.length === 0) return;
  if (running) { pending = true; return; }
  running = true;
  try {
    let guard = 0;
    let firstItems = items;
    // 実行中に届いたイベント(pending)だけ再実行。検証パスは入れず往復を最小化して高速化。
    // （信頼性は「イベントを取りこぼさない」coalescingで担保）
    do {
      pending = false;
      await reconcileOnce(firstItems);
      firstItems = undefined; // 2周目以降は最新をcart.jsで読む
      guard += 1;
    } while (pending && guard < 6); // 安全弁：万一の無限ループ防止
  } finally {
    running = false;
  }
}

// カート操作が確定するたびに同期。event.promise は「操作確定」で解決するので必ず待つ
document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
  if (event && event[INTERNAL_FLAG]) return; // 自分のUI更新イベントは無視
  const p = event && event.promise;
  if (p && typeof p.then === 'function') {
    // 解決値に確定後のカート内容(detail.items)があれば、それを使ってcart.js再取得を省略
    p.then((result) => reconcile(result && result.detail && result.detail.items)).catch(() => reconcile());
  } else {
    Promise.resolve().then(() => reconcile());
  }
});

// 初回：過去のズレがあれば是正
function initialReconcile() {
  if ('requestIdleCallback' in window) window.requestIdleCallback(() => reconcile(), { timeout: 2000 });
  else setTimeout(() => reconcile(), 300);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialReconcile);
else initialReconcile();
